export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0.1 to 1.0
  maxSizeKB?: number; // Maximum file size in KB
  outputFormat?: 'image/jpeg' | 'image/png' | 'image/webp';
}

export interface CompressionResult {
  compressedFile: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  dimensions: {
    original: { width: number; height: number };
    compressed: { width: number; height: number };
  };
  quality: number;
}

export class ImageCompression {
  private static readonly DEFAULT_OPTIONS: Required<CompressionOptions> = {
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.8,
    maxSizeKB: 500, // 500KB max
    outputFormat: 'image/jpeg',
  };

  /**
   * Compress an image file with the given options
   */
  static async compressImage(
    file: File,
    options: CompressionOptions = {}
  ): Promise<CompressionResult> {
    const opts = { ...this.DEFAULT_OPTIONS, ...options };
    
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      img.onload = () => {
        try {
          // Calculate new dimensions while maintaining aspect ratio
          const { width: newWidth, height: newHeight } = this.calculateDimensions(
            img.width,
            img.height,
            opts.maxWidth,
            opts.maxHeight
          );

          // Set canvas dimensions
          canvas.width = newWidth;
          canvas.height = newHeight;

          // Draw and compress the image
          ctx.drawImage(img, 0, 0, newWidth, newHeight);

          // Try different quality levels if file is too large
          this.compressWithQuality(
            canvas,
            file,
            opts,
            (compressedFile, quality) => {
              const originalSize = file.size;
              const compressedSize = compressedFile.size;
              const compressionRatio = ((originalSize - compressedSize) / originalSize) * 100;

              resolve({
                compressedFile,
                originalSize,
                compressedSize,
                compressionRatio,
                dimensions: {
                  original: { width: img.width, height: img.height },
                  compressed: { width: newWidth, height: newHeight },
                },
                quality,
              });
            },
            reject
          );
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      // Load the image
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Calculate new dimensions while maintaining aspect ratio
   */
  private static calculateDimensions(
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number; height: number } {
    let { width, height } = { width: originalWidth, height: originalHeight };

    // Scale down if too wide
    if (width > maxWidth) {
      height = (height * maxWidth) / width;
      width = maxWidth;
    }

    // Scale down if too tall
    if (height > maxHeight) {
      width = (width * maxHeight) / height;
      height = maxHeight;
    }

    return { width: Math.round(width), height: Math.round(height) };
  }

  /**
   * Compress with different quality levels to meet size requirements
   */
  private static compressWithQuality(
    canvas: HTMLCanvasElement,
    originalFile: File,
    options: Required<CompressionOptions>,
    onSuccess: (file: File, quality: number) => void,
    onError: (error: Error) => void
  ): void {
    const maxSizeBytes = options.maxSizeKB * 1024;
    const qualitySteps = [options.quality, 0.7, 0.5, 0.3, 0.1];
    
    let currentStep = 0;

    const tryCompression = () => {
      if (currentStep >= qualitySteps.length) {
        onError(new Error('Could not compress image to required size'));
        return;
      }

      const quality = qualitySteps[currentStep];
      
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            onError(new Error('Failed to compress image'));
            return;
          }

          // Check if size is acceptable
          if (blob.size <= maxSizeBytes || quality <= 0.1) {
            const compressedFile = new File(
              [blob],
              originalFile.name.replace(/\.[^/.]+$/, `.${options.outputFormat.split('/')[1]}`),
              {
                type: options.outputFormat,
                lastModified: Date.now(),
              }
            );
            onSuccess(compressedFile, quality);
          } else {
            // Try next quality level
            currentStep++;
            tryCompression();
          }
        },
        options.outputFormat,
        quality
      );
    };

    tryCompression();
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get compression settings based on file size
   */
  static getCompressionSettings(file: File): CompressionOptions {
    const fileSizeMB = file.size / (1024 * 1024);
    
    if (fileSizeMB > 10) {
      // Very large file - aggressive compression
      return {
        maxWidth: 1280,
        maxHeight: 720,
        quality: 0.6,
        maxSizeKB: 300,
        outputFormat: 'image/jpeg',
      };
    } else if (fileSizeMB > 5) {
      // Large file - moderate compression
      return {
        maxWidth: 1600,
        maxHeight: 900,
        quality: 0.7,
        maxSizeKB: 400,
        outputFormat: 'image/jpeg',
      };
    } else if (fileSizeMB > 2) {
      // Medium file - light compression
      return {
        maxWidth: 1920,
        maxHeight: 1080,
        quality: 0.8,
        maxSizeKB: 500,
        outputFormat: 'image/jpeg',
      };
    } else {
      // Small file - minimal compression
      return {
        maxWidth: 2560,
        maxHeight: 1440,
        quality: 0.9,
        maxSizeKB: 800,
        outputFormat: 'image/jpeg',
      };
    }
  }

  /**
   * Validate if file is a supported image type
   */
  static isSupportedImageType(file: File): boolean {
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    return supportedTypes.includes(file.type.toLowerCase());
  }

  /**
   * Get image dimensions from file
   */
  static getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => {
        reject(new Error('Failed to load image'));
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    });
  }
}
